/** Ultra széles / hátsó kamera — a lehető legnagyobb látómező, kerülve az 1:1 cropot. */

const ULTRA_LABEL = /ultra.?wide|ultrawide|0\.?5x|0\.?5\s|uw\b|széles\s*\(0|grand.?angle|weitwinkel/i;
const FRONT_LABEL = /front|user|selfie|face|self|elülső|frontal|true.?depth/i;
const TELE_LABEL = /telephoto|back telephoto|tele\s|tele\b|2×|2x|3×|3x|5×|5x/i;
/** iPhone / Android fő (1×) hátsó — nem ultra, nem tele */
const MAIN_BACK_LABEL = /^back camera$|^camera 2, facing back$|^hátsó kamera$/i;

export function cameraKindFromLabel(label = "") {
  const L = (label || "").trim();
  if (FRONT_LABEL.test(L)) return "front";
  if (TELE_LABEL.test(L)) return "tele";
  if (ULTRA_LABEL.test(L)) return "ultra";
  if (MAIN_BACK_LABEL.test(L) || (/back/i.test(L) && /wide angle/i.test(L) && !/ultra/i.test(L))) return "main";
  if (/back|rear|environment|hátsó/i.test(L)) return "main";
  return "other";
}

export function cameraDisplayLabel(label = "") {
  const kind = cameraKindFromLabel(label);
  if (kind === "main") return "1× fő";
  if (kind === "ultra") return "0,5× széles";
  if (kind === "tele") return "Tele";
  return (label || "Hátsó kamera").slice(0, 28);
}

function cameraPickRank(label = "") {
  const kind = cameraKindFromLabel(label);
  if (kind === "main") return 0;
  if (kind === "other") return 15;
  if (kind === "ultra") return 40;
  if (kind === "tele") return 100;
  return 50;
}

/** Különböző getUserMedia profilok — a szélesebb képarány előbb. */
const CONSTRAINT_PROFILES = [
  {
    name: "16:9 HD",
    video: {
      width: { ideal: 1920, min: 1280 },
      height: { ideal: 1080, min: 720 },
      aspectRatio: { ideal: 16 / 9, min: 1.45 },
      facingMode: { ideal: "environment" },
    },
  },
  {
    name: "16:9",
    video: {
      width: { ideal: 1280 },
      height: { ideal: 720 },
      aspectRatio: { ideal: 16 / 9 },
      facingMode: { ideal: "environment" },
    },
  },
  {
    name: "4:3",
    video: {
      width: { ideal: 1280, min: 640 },
      height: { ideal: 960, min: 480 },
      aspectRatio: { ideal: 4 / 3, min: 1.2 },
      facingMode: { ideal: "environment" },
    },
  },
  {
    name: "max",
    video: {
      width: { ideal: 1920, min: 640 },
      height: { ideal: 1080, min: 480 },
      facingMode: { ideal: "environment" },
    },
  },
  {
    name: "minimal",
    video: { facingMode: { ideal: "environment" } },
  },
];

function scoreStream(track) {
  const settings = track.getSettings?.() || {};
  const caps = track.getCapabilities?.() || {};
  const label = track.label || "";
  let score = 0;

  if (ULTRA_LABEL.test(label)) score += 2_000_000_000;
  if (TELE_LABEL.test(label)) score -= 1_500_000_000;
  if (FRONT_LABEL.test(label)) score -= 800_000_000;

  const w = settings.width || 0;
  const h = settings.height || 0;
  const ratio = w && h ? w / h : 0;

  // Széles látómező jutalmazása — 1:1 (négyzet crop) büntetése
  if (ratio >= 1.65) score += 80_000_000;
  else if (ratio >= 1.45) score += 50_000_000;
  else if (ratio >= 1.28) score += 25_000_000;
  else if (ratio >= 0.95 && ratio <= 1.08) score -= 100_000_000;

  if (w && h) score += w * h * 0.002;

  const zoomMin = caps.zoom?.min;
  if (typeof zoomMin === "number" && zoomMin < 1) score += (1 - zoomMin) * 120_000_000;

  const zoomSetting = settings.zoom;
  if (typeof zoomSetting === "number" && zoomSetting < 1) score += (1 - zoomSetting) * 80_000_000;

  const fov = caps.fieldOfView;
  if (typeof fov === "number") score += fov * 2_000_000;
  else if (fov?.max) score += fov.max * 2_000_000;

  const focalMin = caps.focalLength?.min;
  if (typeof focalMin === "number") score += Math.max(0, 40 - focalMin) * 200_000;

  return score;
}

export function describeStream(stream) {
  const track = stream?.getVideoTracks?.()?.[0];
  if (!track) return { width: 0, height: 0, ratio: 0, label: "", isSquare: false, isWide: false, zoom: null };
  const s = track.getSettings?.() || {};
  const w = s.width || 0;
  const h = s.height || 0;
  const ratio = w && h ? w / h : 0;
  return {
    width: w,
    height: h,
    ratio: Math.round(ratio * 100) / 100,
    label: track.label || "",
    isSquare: ratio >= 0.92 && ratio <= 1.08,
    isWide: ratio >= 1.35,
    zoom: s.zoom ?? null,
    score: scoreStream(track),
  };
}

export async function applyWidestZoom(stream) {
  const track = stream?.getVideoTracks?.()?.[0];
  if (!track?.applyConstraints || !track.getCapabilities) return;
  try {
    const caps = track.getCapabilities();
    if (caps.zoom == null) return;
    const zmin = typeof caps.zoom === "object" ? caps.zoom.min ?? 1 : 1;
    const zmax = typeof caps.zoom === "object" ? caps.zoom.max ?? 1 : 1;
    const candidates = [zmin, 0.5, 0.6, 0.7, 1].filter(
      (z, i, arr) => z >= zmin - 0.01 && z <= zmax + 0.01 && arr.indexOf(z) === i,
    );
    for (const z of candidates) {
      try {
        await track.applyConstraints({ advanced: [{ zoom: z }] });
        return;
      } catch (_) {
        /* próbáljuk a következő zoom értéket */
      }
    }
  } catch (_) { /* nem minden eszközön */ }
}

export async function listBackCameras() {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  let devices = await navigator.mediaDevices.enumerateDevices();
  if (!devices.some((d) => d.label)) {
    try {
      const tmp = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      tmp.getTracks().forEach((t) => t.stop());
      devices = await navigator.mediaDevices.enumerateDevices();
    } catch (_) { /* */ }
  }
  return devices
    .filter((d) => d.kind === "videoinput")
    .filter((d) => !FRONT_LABEL.test(d.label || ""))
    .filter((d) => cameraKindFromLabel(d.label) !== "tele")
    .map((d) => ({
      deviceId: d.deviceId,
      label: d.label || "Hátsó kamera",
      isUltra: cameraKindFromLabel(d.label) === "ultra",
      isMain: cameraKindFromLabel(d.label) === "main",
      displayLabel: cameraDisplayLabel(d.label),
    }))
    .sort((a, b) => cameraPickRank(a.label) - cameraPickRank(b.label));
}

async function tryOpenWithProfiles(deviceId) {
  let best = null;
  let bestScore = -Infinity;
  let bestProfile = "";

  for (const profile of CONSTRAINT_PROFILES) {
    const video = deviceId
      ? { ...profile.video, deviceId: { exact: deviceId } }
      : { ...profile.video };
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video, audio: false });
    } catch (_) {
      continue;
    }
    await applyWidestZoom(stream);
    const track = stream.getVideoTracks()[0];
    const s = scoreStream(track);
    if (s > bestScore) {
      if (best) best.getTracks().forEach((t) => t.stop());
      best = stream;
      bestScore = s;
      bestProfile = profile.name;
      best._deviceId = deviceId;
      best._profile = profile.name;
    } else {
      stream.getTracks().forEach((t) => t.stop());
    }
  }

  return best ? { stream: best, score: bestScore, profile: bestProfile } : null;
}

/** Megpróbálja az összes hátsó kamerát + profilt, a legszélesebbet adja vissza. */
/** 1× — mint a natív Kamera app (nem zoomol be). */
export async function applyDefaultZoom(stream) {
  const track = stream?.getVideoTracks()?.[0];
  if (!track?.applyConstraints || !track.getCapabilities) return;
  try {
    const caps = track.getCapabilities();
    if (caps.zoom == null) return;
    const zmin = typeof caps.zoom === "object" ? caps.zoom.min ?? 1 : 1;
    const zmax = typeof caps.zoom === "object" ? caps.zoom.max ?? 1 : 1;
    const tryZoom = [1, zmin, zmax].filter(
      (z, i, arr) => z >= zmin - 0.02 && z <= zmax + 0.02 && arr.indexOf(z) === i,
    );
    for (const target of tryZoom) {
      try {
        await track.applyConstraints({ advanced: [{ zoom: target }] });
        const got = track.getSettings?.()?.zoom;
        if (got == null || Math.abs(got - target) < 0.15 || target === 1) return;
      } catch (_) { /* következő */ }
    }
  } catch (_) { /* eszközfüggő */ }
}

/**
 * @param {string|null} preferredDeviceId
 * @param {{ allowUltraWide?: boolean }} opts — 360° alapból 1× fő; opcionális 0,5×
 */
export async function openStandardCamera(preferredDeviceId, opts = {}) {
  const { allowUltraWide = false } = opts;
  const cameras = await listBackCameras();
  const deviceOrder = [];
  if (preferredDeviceId) deviceOrder.push(preferredDeviceId);
  const ranked = allowUltraWide
    ? cameras
    : cameras.filter((c) => !c.isUltra);
  for (const cam of ranked) {
    if (!deviceOrder.includes(cam.deviceId)) deviceOrder.push(cam.deviceId);
  }
  if (!deviceOrder.length) deviceOrder.push(null);

  const landscapeFirst = isLandscapeViewport()
    ? [
      { width: { ideal: 1920, min: 1280 }, height: { ideal: 1080, min: 720 }, aspectRatio: { ideal: 16 / 9 } },
      { width: { ideal: 1280 }, height: { ideal: 720 }, aspectRatio: { ideal: 16 / 9 } },
    ]
    : [];

  // iOS: magas felbontás gyakran digitális crop — előbb laza, majd HD
  const profiles = [
    ...landscapeFirst,
    ...(isLikelyIOS()
      ? [
        { facingMode: { ideal: "environment" } },
        { width: { ideal: 1280 }, height: { ideal: 720 }, aspectRatio: { ideal: 16 / 9 } },
        { width: { ideal: 1920 }, height: { ideal: 1080 } },
      ]
      : [
        { width: { ideal: 1280 }, height: { ideal: 720 } },
        { width: { ideal: 1920 }, height: { ideal: 1080 } },
        { facingMode: { ideal: "environment" } },
      ]),
  ];

  for (const deviceId of deviceOrder) {
    for (const extra of profiles) {
      const video = { ...extra };
      if (!video.facingMode) video.facingMode = { ideal: "environment" };
      if (deviceId) video.deviceId = { exact: deviceId };
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video, audio: false });
        await applyDefaultZoom(stream);
        stream._deviceId = deviceId;
        stream._cameraLabel = cameras.find((c) => c.deviceId === deviceId)?.label || "";
        return stream;
      } catch (_) {
        continue;
      }
    }
  }
  throw new Error("Kamera nem elérhető");
}

/** Alapértelmezett 1× fő hátsó deviceId (iPhone „Back Camera”). */
export function defaultMainBackDeviceId(cameras) {
  if (!cameras?.length) return null;
  const main = cameras.find((c) => c.isMain);
  if (main) return main.deviceId;
  const notUltra = cameras.find((c) => !c.isUltra);
  return (notUltra || cameras[0]).deviceId;
}

/** 360° preview — ultra-wide ha van, különben fő hátsó (zoommal szélesíthető). */
export function defaultWidestBackDeviceId(cameras) {
  if (!cameras?.length) return null;
  const ultra = cameras.find((c) => c.isUltra);
  if (ultra) return ultra.deviceId;
  return defaultMainBackDeviceId(cameras);
}

async function openWideDevice(deviceId) {
  const result = await tryOpenWithProfiles(deviceId);
  if (result?.stream) return result.stream;
  const stream = await openStandardCamera(deviceId, { allowUltraWide: true });
  await applyWidestZoom(stream);
  return stream;
}

/**
 * 360° walkaround kamera — alapból max látómező; Lencse → 1× fő (`forceMainLens`).
 */
export async function open360Camera(preferredDeviceId, cameras = [], opts = {}) {
  const { forceMainLens = false } = opts;
  const list = cameras.length ? cameras : await listBackCameras();
  const cam = preferredDeviceId ? list.find((c) => c.deviceId === preferredDeviceId) : null;

  if (forceMainLens && preferredDeviceId) {
    return openStandardCamera(preferredDeviceId, { allowUltraWide: false });
  }
  if (cam?.isUltra) {
    return openWideDevice(preferredDeviceId);
  }
  if (preferredDeviceId && cam) {
    const stream = await openStandardCamera(preferredDeviceId, { allowUltraWide: false });
    await applyWidestZoom(stream);
    return stream;
  }
  return openWidestCamera(preferredDeviceId ?? null);
}

/** UI váltás: max. normál + széles lencse, duplikált „1× fő” gombok nélkül. */
export function switchableBackCameras(cameras) {
  if (!cameras?.length) return [];
  const main = cameras.find((c) => c.isMain) || cameras.find((c) => !c.isUltra) || cameras[0];
  const ultra = cameras.find((c) => c.isUltra && c.deviceId !== main?.deviceId);
  const out = [];
  if (main) out.push(main);
  if (ultra) out.push(ultra);
  return out.length ? out : [cameras[0]];
}

export function isLandscapeViewport() {
  if (typeof window === "undefined") return true;
  try {
    const o = window.screen?.orientation?.type;
    if (o) return o.startsWith("landscape");
  } catch (_) { /* */ }
  if (typeof window.matchMedia === "function") {
    if (window.matchMedia("(orientation: landscape)").matches) return true;
  }
  return window.innerWidth > window.innerHeight;
}

export async function openWidestCamera(preferredDeviceId) {
  const tryDevices = [];

  if (preferredDeviceId) {
    tryDevices.push(preferredDeviceId);
  }

  const cameras = await listBackCameras();
  const ordered = [
    ...cameras.filter((c) => c.isUltra),
    ...cameras.filter((c) => !c.isUltra),
  ];
  for (const cam of ordered) {
    if (!tryDevices.includes(cam.deviceId)) tryDevices.push(cam.deviceId);
  }
  if (tryDevices.length === 0) tryDevices.push(null);

  let best = null;
  let bestScore = -Infinity;

  for (const deviceId of tryDevices) {
    const result = await tryOpenWithProfiles(deviceId);
    if (!result) continue;
    if (result.score > bestScore) {
      if (best) best.getTracks().forEach((t) => t.stop());
      best = result.stream;
      bestScore = result.score;
    } else {
      result.stream.getTracks().forEach((t) => t.stop());
    }
  }

  if (best) return best;

  const fallback = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: { ideal: "environment" } },
    audio: false,
  });
  await applyWidestZoom(fallback);
  return fallback;
}

export function isLikelyMobile() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export function isLikelyIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}
